'use client'

import React from 'react'

import { Box, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Button } from '../Button'
import {
  formBodyStyles,
  formContainerStyles,
  formDescriptionStyles,
  formFooterStyles,
  formHeaderStyles,
  formSectionBodyStyles,
  formSectionDescriptionStyles,
  formSectionLabelAreaStyles,
  formSectionStyles,
  formSectionTitleStyles,
  formTitleStyles,
} from './MultiPartForm.styles'

export type FormSectionDef = {
  title: string
  description?: string
  body: React.ReactNode
}

export type MultiPartFormProps = {
  title?: string
  description?: string
  sections: FormSectionDef[]
  onComplete: () => void
  completeButtonText?: string
  isCompleteDisabled?: boolean
  isCompleteLoading?: boolean
  colorScheme?: ColorScheme
  /** Fills the parent and pins the action row to its bottom. */
  fillHeight?: boolean
}

export type FormSectionProps = {
  title: string
  description?: string
  colorScheme?: ColorScheme
  children: React.ReactNode
}

export const FormSection = ({
  title,
  description,
  colorScheme = 'dark',
  children,
}: FormSectionProps) => (
  <Box {...formSectionStyles}>
    <Box {...formSectionLabelAreaStyles}>
      <Text {...formSectionTitleStyles(colorScheme)}>{title}</Text>
      {description && (
        <Text {...formSectionDescriptionStyles(colorScheme)}>
          {description}
        </Text>
      )}
    </Box>
    <Box {...formSectionBodyStyles}>{children}</Box>
  </Box>
)

export const MultiPartForm = ({
  title,
  description,
  sections,
  onComplete,
  completeButtonText = 'Submit',
  isCompleteDisabled,
  isCompleteLoading,
  colorScheme = 'dark',
  fillHeight = false,
}: MultiPartFormProps) => (
  <Box {...formContainerStyles(colorScheme, fillHeight)}>
    {(title || description) && (
      <Box {...formHeaderStyles}>
        {title && <Text {...formTitleStyles(colorScheme)}>{title}</Text>}
        {description && (
          <Text {...formDescriptionStyles(colorScheme)}>{description}</Text>
        )}
      </Box>
    )}
    <Box {...formBodyStyles}>
      {sections.map((section, index) => (
        <FormSection
          colorScheme={colorScheme}
          description={section.description}
          key={index}
          title={section.title}
        >
          {section.body}
        </FormSection>
      ))}
    </Box>
    <Box {...formFooterStyles(fillHeight)}>
      <Button
        colorScheme={colorScheme}
        disabled={isCompleteDisabled}
        isLoading={isCompleteLoading}
        onClick={onComplete}
        variant='primary'
      >
        {completeButtonText}
      </Button>
    </Box>
  </Box>
)
